import { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { buildCheckoutReturnUrl, startProviderCheckout } from '../../../lib/billing/checkoutClient';
import {
  type CheckoutIntent,
  type CheckoutIntentCycle,
  type CheckoutIntentPlan,
  createCheckoutIntent,
  getCheckoutIntent,
  saveCheckoutIntent,
} from '../../../lib/billing/checkoutIntent';
import { formatCurrency } from '../../../lib/format';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import {
  type CheckoutPresentationSignal,
  useCheckoutActivation,
} from '../hooks/useCheckoutActivation';
import { useCheckoutReadiness, useSubscription } from '../hooks/useSettings';
import { SettingsSection } from './SettingsSection';

const SELF_SERVE_TIERS: CheckoutIntentPlan[] = ['starter', 'growth', 'professional', 'business'];
const KNOWN_PLANS = new Set<string>([...SELF_SERVE_TIERS, 'enterprise']);
const TERMINAL_ATTEMPT_STATUSES = new Set(['approved', 'expired', 'voided']);

function usePlanName() {
  const { t } = useTranslation();
  return (plan: string | null | undefined): string => {
    if (!plan) return t('settings.billing.noPlan');
    return KNOWN_PLANS.has(plan) ? t(`landing.pricing.plans.${plan}.name`) : plan;
  };
}

export function BillingSection({
  checkoutReturn,
}: Readonly<{ checkoutReturn?: 'return' | 'cancel' }>) {
  const { t } = useTranslation();
  const { currentOrganization, backendUser } = useAuth();
  const orgId = currentOrganization?.id;
  const planName = usePlanName();
  const [cycle, setCycle] = useState<CheckoutIntentCycle>('monthly');
  const [intent, setIntent] = useState<CheckoutIntent | null>(null);
  const [isLaunching, setIsLaunching] = useState(false);
  const [hasCheckoutError, setHasCheckoutError] = useState(false);
  const [presentationSignal, setPresentationSignal] = useState<CheckoutPresentationSignal | null>(
    checkoutReturn ? 'returned' : null,
  );
  const resumedIntentRef = useRef<string | null>(null);
  const memberships = backendUser?.organizationMembers ?? backendUser?.organizations ?? [];
  const currentMembership = memberships.find(
    (membership) => membership.organization.id === currentOrganization?.id,
  );
  const canManageBilling = ['owner', 'admin'].includes(currentMembership?.role ?? '');

  const { data: subscription } = useSubscription(orgId);
  const { data: readiness, refetch: refetchReadiness } = useCheckoutReadiness(
    orgId,
    canManageBilling,
  );
  const activation = useCheckoutActivation(orgId, intent, { presentationSignal });

  const currentPlan = subscription?.plan ?? null;
  const currentStatus = subscription?.status ?? null;
  const pendingPlan = subscription?.pendingPlan ?? null;
  const pendingCycle = subscription?.pendingCycle ?? null;
  const showPendingBanner =
    !!intent &&
    (!intent.organizationId || intent.organizationId === orgId) &&
    pendingPlan === intent.plan &&
    pendingCycle === intent.cycle &&
    pendingPlan !== currentPlan;
  const hasCheckoutEligibleSubscription =
    subscription?.status === 'active' &&
    subscription.effectivePlan === 'free' &&
    !subscription.externalCustomerId &&
    !subscription.externalSubscriptionId &&
    ((subscription.plan === 'free' &&
      ['stripe', null, undefined].includes(subscription.provider)) ||
      (subscription.provider === 'wompi' &&
        Date.parse(subscription.currentPeriodEnd) <= Date.now()));
  const checkoutConfigured =
    !!readiness &&
    ['wompi', 'stripe'].includes(readiness.provider) &&
    readiness.ready &&
    readiness.eligible;

  useEffect(() => {
    if (checkoutReturn) setPresentationSignal('returned');
  }, [checkoutReturn]);

  useEffect(() => {
    let active = true;
    void getCheckoutIntent()
      .then((storedIntent) => {
        if (!active || (storedIntent?.organizationId && storedIntent.organizationId !== orgId)) {
          return;
        }
        setIntent(storedIntent);
        if (storedIntent) setCycle(storedIntent.cycle);
      })
      .catch(() => {
        if (active) setHasCheckoutError(true);
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  useEffect(() => {
    if (TERMINAL_ATTEMPT_STATUSES.has(activation.status)) {
      setIntent(null);
    }
  }, [activation.status]);

  const launchCheckout = useCallback(
    async (checkoutIntent: CheckoutIntent) => {
      if (!orgId || !canManageBilling || !hasCheckoutEligibleSubscription) {
        return;
      }

      const successUrl = buildCheckoutReturnUrl('/settings?section=billing&checkout=return');
      const cancelUrl = buildCheckoutReturnUrl('/settings?section=billing&checkout=cancel');
      if (!successUrl || !cancelUrl) {
        setHasCheckoutError(true);
        return;
      }

      setHasCheckoutError(false);
      setPresentationSignal(null);
      setIsLaunching(true);
      try {
        const { data: currentReadiness } = await refetchReadiness();
        const quote = currentReadiness?.quotes[checkoutIntent.plan]?.[checkoutIntent.cycle];
        const isCurrentQuoteAvailable =
          !!currentReadiness &&
          ['wompi', 'stripe'].includes(currentReadiness.provider) &&
          currentReadiness.ready &&
          currentReadiness.eligible &&
          currentReadiness.plans[checkoutIntent.plan]?.[checkoutIntent.cycle] === true &&
          quote?.provider === currentReadiness.provider &&
          quote.currencyCode === currentReadiness.currencyCode &&
          !!quote;
        if (!isCurrentQuoteAvailable || !quote) {
          setHasCheckoutError(true);
          return;
        }
        const outcome = await startProviderCheckout({
          organizationId: orgId,
          intent: checkoutIntent,
          successUrl,
          cancelUrl,
          expectedRoute: {
            provider: quote.provider,
            ...(quote.priceReference ? { priceReference: quote.priceReference } : {}),
          },
        });
        setIntent(await getCheckoutIntent());
        if (outcome.status === 'dismissed') setPresentationSignal('dismissed');
        if (outcome.status === 'unavailable') {
          setHasCheckoutError(true);
          return;
        }
      } catch {
        setHasCheckoutError(true);
      } finally {
        setIsLaunching(false);
      }
    },
    [canManageBilling, hasCheckoutEligibleSubscription, orgId, refetchReadiness],
  );

  useEffect(() => {
    if (
      !intent ||
      intent.attemptId ||
      resumedIntentRef.current === intent.idempotencyKey ||
      !orgId ||
      !canManageBilling ||
      !hasCheckoutEligibleSubscription ||
      !checkoutConfigured ||
      readiness?.plans[intent.plan]?.[intent.cycle] !== true
    ) {
      return;
    }
    resumedIntentRef.current = intent.idempotencyKey;
    void launchCheckout(intent);
  }, [
    canManageBilling,
    checkoutConfigured,
    hasCheckoutEligibleSubscription,
    intent,
    launchCheckout,
    orgId,
    readiness,
  ]);

  const selectPlan = async (plan: CheckoutIntentPlan) => {
    const matchingIntent =
      intent?.plan === plan && intent.cycle === cycle ? intent : createCheckoutIntent(plan, cycle);
    resumedIntentRef.current = matchingIntent.idempotencyKey;
    if (matchingIntent !== intent) {
      await saveCheckoutIntent(matchingIntent);
      setIntent(matchingIntent);
    }
    await launchCheckout(matchingIntent);
  };

  const statusMessage = hasCheckoutError
    ? t('settings.billing.checkoutUnavailable')
    : activation.presentationState === 'recoverable'
      ? t('settings.billing.paymentIncomplete')
      : activation.presentationState === 'finalizing'
        ? t('settings.billing.finalizing')
        : null;

  return (
    <SettingsSection title={t('settings.billing.title')}>
      <VStack spacing="lg">
        <HStack spacing="md" align="center" justify="between">
          <VStack spacing="xxs">
            <Text variant="caption" color={colors.text.tertiary}>
              {t('settings.billing.currentPlan')}
            </Text>
            <Text
              testID="billing_current_plan"
              variant="body"
              weight="semibold"
              color={colors.text.primary}
            >
              {planName(currentPlan)}
            </Text>
          </VStack>
          <Text testID="billing_current_status" variant="caption" color={colors.text.secondary}>
            {currentStatus ?? t('settings.billing.noStatus')}
          </Text>
        </HStack>

        <Text testID="billing_currency_note" variant="caption" color={colors.text.tertiary}>
          {`${t('settings.billing.currencyNote')} ${readiness?.currencyCode ?? ''}`}
        </Text>

        {!checkoutConfigured || !canManageBilling || !hasCheckoutEligibleSubscription ? (
          <View testID="billing_checkout_unavailable" style={styles.unavailableNotice}>
            <Text variant="bodySmall" color={colors.status.warning.light} selectable>
              {t('settings.billing.checkoutUnavailable')}
            </Text>
          </View>
        ) : null}

        {statusMessage ? (
          <View
            testID="billing_checkout_status"
            style={styles.banner}
            accessibilityLiveRegion="polite"
          >
            <Text variant="bodySmall" color={colors.text.primary} selectable>
              {statusMessage}
            </Text>
          </View>
        ) : null}

        {showPendingBanner ? (
          <View testID="billing_pending_banner" style={styles.banner}>
            <Text variant="bodySmall" color={colors.text.primary} weight="medium">
              {t('settings.billing.pendingSelected')} {planName(pendingPlan)}
            </Text>
          </View>
        ) : null}

        <HStack spacing="sm" accessibilityRole="tablist">
          {(['monthly', 'yearly'] as const).map((billingCycle) => (
            <Pressable
              key={billingCycle}
              testID={`billing_cycle_${billingCycle}`}
              onPress={() => setCycle(billingCycle)}
              accessibilityRole="tab"
              accessibilityState={{ selected: cycle === billingCycle }}
              style={[styles.cycleCta, cycle === billingCycle && styles.cycleCtaSelected]}
            >
              <Text variant="bodySmall" weight="semibold" color={colors.text.primary}>
                {t(`settings.billing.${billingCycle}`)}
              </Text>
            </Pressable>
          ))}
        </HStack>

        <VStack spacing="sm">
          {SELF_SERVE_TIERS.map((tier) => {
            const isCurrent = tier === currentPlan && subscription?.effectivePlan !== 'free';
            const isIntentPlan = intent?.plan === tier && intent.cycle === cycle;
            const quote = readiness?.quotes[tier]?.[cycle];
            const planAvailable = readiness?.plans[tier]?.[cycle] === true && !!quote;
            const disabled =
              isLaunching ||
              isCurrent ||
              !checkoutConfigured ||
              !canManageBilling ||
              !hasCheckoutEligibleSubscription ||
              !planAvailable ||
              (!!intent?.attemptId && !isIntentPlan) ||
              activation.status === 'approved';
            return (
              <HStack
                key={tier}
                spacing="md"
                align="center"
                justify="between"
                style={styles.planRow}
              >
                <VStack spacing="xxs">
                  <Text variant="body" color={colors.text.primary}>
                    {planName(tier)}
                  </Text>
                  {quote ? (
                    <Text
                      testID={`billing_quote_${tier}`}
                      variant="caption"
                      color={colors.text.secondary}
                    >
                      {formatCurrency(quote.amountInCents / 100, quote.currencyCode)}
                    </Text>
                  ) : null}
                </VStack>
                <Pressable
                  testID={`billing_select_${tier}`}
                  disabled={disabled}
                  onPress={() => void selectPlan(tier)}
                  accessibilityRole="button"
                  accessibilityState={{ disabled, busy: isLaunching }}
                  accessibilityHint={
                    disabled ? t('settings.billing.checkoutUnavailable') : undefined
                  }
                  style={[styles.selectCta, disabled && styles.selectCtaDisabled]}
                >
                  <Text variant="bodySmall" weight="semibold" color={colors.text.primary}>
                    {isCurrent
                      ? t('settings.billing.currentPlanCta')
                      : isIntentPlan && intent?.attemptId
                        ? t('settings.billing.completePayment')
                        : t('settings.billing.selectPlan')}
                  </Text>
                </Pressable>
              </HStack>
            );
          })}

          <HStack spacing="md" align="center" justify="between" style={styles.planRow}>
            <Text variant="body" color={colors.text.primary}>
              {planName('enterprise')}
            </Text>
            <View testID="billing_contact_sales" style={styles.selectCta}>
              <Text variant="bodySmall" weight="semibold" color={colors.text.secondary}>
                {t('settings.billing.contactSales')}
              </Text>
            </View>
          </HStack>
        </VStack>
      </VStack>
    </SettingsSection>
  );
}

const styles = StyleSheet.create({
  unavailableNotice: {
    padding: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.status.warning.border,
    backgroundColor: colors.status.warning.bg,
  },
  banner: {
    padding: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.primary.borderActive,
    backgroundColor: colors.primary.muted,
  },
  cycleCta: {
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  cycleCtaSelected: {
    borderColor: colors.primary.borderActive,
    backgroundColor: colors.primary.muted,
  },
  planRow: {
    minHeight: 52,
    paddingVertical: tokens.spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border.subtle,
  },
  selectCta: {
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  selectCtaDisabled: {
    opacity: 0.5,
  },
});
